import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconBriefcase, IconCheck, IconTrash, IconUser } from '@tabler/icons-react';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { iconProps } from 'components/icon-props';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import { useLoadMail } from 'modules/Mails/hooks';
import { getLastMail } from 'modules/Mails/utils/thread';
import { useHistory, useParams } from 'react-router';
import { useMailDetailActions } from '../hooks/use-mail-detail-actions';
import { AssignPill } from './AssignPill';
import { MailDetailNavigation } from './MailDetailNavigation';
import { MailDetailQuickActions } from './MailDetailQuickActions';

type MailDetailHeaderProps = {
  openReply: (options?: { focusAiPrompt?: boolean }) => void;
};

export const MailDetailHeader = ({ openReply: _openReply }: MailDetailHeaderProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const { thread, refetchThread, isThreadLoading, isThreadRefetching } = useLoadMail(id);
  const {
    handleToggleStatus,
    handleAssign,
    handleDelete,
    isDeleteMailLoading,
    isUpdateThreadLoading,
  } = useMailDetailActions({ id, thread, refetchThread });

  const subject = getLastMail(thread)?.subject ?? '';
  const isProcessed = thread.isProcessed;
  const isStatusUpdating = isThreadLoading || isThreadRefetching || isUpdateThreadLoading;
  const debtor = thread.debtor;

  const statusLabel = t(tKeysLoose.MAILS.STATUS[isProcessed ? 'PROCESSED' : 'UNPROCESSED']);
  const statusToggleTooltip = t(
    i18nKeys.EXTERNAL_MAIL.ACTIONS[isProcessed ? 'UNPROCESS' : 'PROCESS'],
  );

  const debtorAssignedTo = debtor ? { label: debtor.fullName, color: 'violet' } : null;

  const accountManagerAssignedTo = thread.accountManager
    ? {
        label: thread.accountManager.name.full.trim(),
        initials: thread.accountManager.name.initials.toUpperCase() || '?',
        color: 'blue',
        avatarSrc: thread.accountManager.profilePicture ?? undefined,
      }
    : null;

  const badgeIcon = isStatusUpdating ? (
    <Loader size={10} color={isProcessed ? 'green' : 'blue'} />
  ) : isProcessed ? (
    <IconCheck {...iconProps(12)} />
  ) : (
    <Box w={6} h={6} bg="blue.6" style={{ borderRadius: 999 }} />
  );

  const debtorPill = (
    <AssignPill
      assignedTo={debtorAssignedTo}
      emptyLabel={t(i18nKeys.EXTERNAL_MAIL.SELECT_DEBTOR)}
      emptyIcon={IconUser}
      onClick={() => handleAssign({ trigger: 'button' })}
    />
  );

  return (
    <Stack
      gap="space2"
      px="space6"
      py="space3"
      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
    >
      <Group justify="space-between" wrap="nowrap" gap="space2">
        <Group gap="space2" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Tooltip label={t(i18nKeys.BACK)}>
            <ActionIcon variant="subtle" color="gray" onClick={history.goBack}>
              <IconArrowLeft {...iconProps()} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t(i18nKeys.DELETE)}>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={handleDelete}
              loading={isDeleteMailLoading}
              aria-label={t(i18nKeys.DELETE)}
            >
              <IconTrash {...iconProps()} />
            </ActionIcon>
          </Tooltip>
          <Divider orientation="vertical" />
          <Tooltip
            label={subject}
            disabled={!subject}
            multiline
            maw={420}
            withinPortal
            openDelay={1500}
          >
            <Title
              order={5}
              fw={400}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: '0 1 auto',
              }}
            >
              {subject || (
                <Text c="dimmed" span>
                  —
                </Text>
              )}
            </Title>
          </Tooltip>
          <Tooltip label={statusToggleTooltip}>
            <Badge
              variant="light"
              color={isProcessed ? 'green' : 'blue'}
              size="md"
              leftSection={badgeIcon}
              onClick={() => handleToggleStatus({ trigger: 'button' })}
              style={{ cursor: 'pointer' }}
            >
              {statusLabel}
            </Badge>
          </Tooltip>
        </Group>
        <Group gap="space2" wrap="nowrap">
          {debtor && <MailDetailQuickActions debtorId={debtor.id} />}
          <MailDetailNavigation />
        </Group>
      </Group>

      <Group gap="space2" wrap="wrap" pl={36}>
        {debtor ? (
          <ClientHoverCard debtorId={debtor.id} debtorName={debtor.fullName}>
            <span style={{ display: 'inline-flex' }}>{debtorPill}</span>
          </ClientHoverCard>
        ) : (
          debtorPill
        )}

        <AssignPill
          assignedTo={accountManagerAssignedTo}
          emptyLabel={t(i18nKeys.ASSIGN_ACCOUNT_MANAGER_SHORT)}
          emptyIcon={IconBriefcase}
          onClick={() => handleAssign({ trigger: 'button' })}
        />
      </Group>
    </Stack>
  );
};
